import { useEffect, useState } from "react";
import { ago, readAlertCard, type AlertCardRef, type WatchlistCardRef } from "./cards";
import { useDataClient, type Recipient } from "./client";
import { CloseIcon } from "./icons";

// Alerts and watchlists (unified alerts, 2026-10-24). They are created only by the agent, in
// the desktop chat or a relayed chat; these views show them and pause / resume alerts. Every
// alert is evaluated in the user's desktop app.

const ROUTE_LABEL: Record<string, string> = { symbolic: "Symbolic", agent: "Agent" };

function routeLabel(r: string) {
  return ROUTE_LABEL[r] ?? "Alert";
}

function channels(c: AlertCardRef) {
  return ["desktop", c.email ? "email" : null, c.whatsapp ? "WhatsApp" : null].filter(Boolean).join(", ");
}

/** Chat card for an alert the agent made: opens the Alerts pane on it. */
export function AlertCard({ card, onOpen }: { card: AlertCardRef; onOpen: (alertId: string) => void }) {
  const target = card.watchlist ? `watchlist ${card.watchlist}` : `${card.covers} symbol${card.covers === 1 ? "" : "s"}`;
  return (
    <button className="bt-card" onClick={() => onOpen(card.alertId)} title="Open the alert">
      <span className={`bt-card-kind alert ${card.route}`}>{routeLabel(card.route)}</span>
      <span className="bt-card-main">
        <b>{card.name}</b>
        <span className="bt-card-sub">
          {target} · {card.schedule}
          {card.status !== "active" ? ` · ${card.status}` : ""}
        </span>
      </span>
      <span className="bt-card-stat">
        <span className="dim">fired </span>
        {ago(card.lastFiredAt)}
      </span>
    </button>
  );
}

/** Chat card for a watchlist the agent made. */
export function WatchlistCard({ card, onOpen }: { card: WatchlistCardRef; onOpen: (watchlistId: string) => void }) {
  return (
    <button className="bt-card" onClick={() => onOpen(card.watchlistId)} title="Open the watchlist">
      <span className="bt-card-kind watchlist">Watchlist</span>
      <span className="bt-card-main">
        <b>{card.name}</b>
        <span className="bt-card-sub">
          {card.symbols.join(", ")}
          {card.count > card.symbols.length ? ` +${card.count - card.symbols.length}` : ""}
        </span>
      </span>
      <span className="bt-card-stat">
        {card.count}
        <span className="dim"> symbols</span>
      </span>
    </button>
  );
}

export type AlertsFocus = { kind: "alert"; id: string } | { kind: "watchlist"; id: string } | null;

/** Right-side pane: Alerts · Watchlists, with one alert's or watchlist's detail. */
export function AlertsPane({ focus, onClose }: { focus: AlertsFocus; onClose: () => void }) {
  const client = useDataClient();
  const [tab, setTab] = useState<"alerts" | "watchlists">(focus?.kind === "watchlist" ? "watchlists" : "alerts");
  const [open, setOpen] = useState<AlertsFocus>(focus);
  const [overview, setOverview] = useState<any>(null);
  const [err, setErr] = useState("");

  const load = () =>
    client
      .alertsOverview()
      .then(setOverview)
      .catch((e) => setErr(String(e)));

  useEffect(() => {
    setOpen(focus);
    if (focus) setTab(focus.kind === "watchlist" ? "watchlists" : "alerts");
  }, [focus?.kind, focus?.id]);

  useEffect(() => {
    load();
    return client.onAlertsUpdate(load);
  }, []);

  const alerts: AlertCardRef[] = (overview?.alerts ?? []).map((a: any) => readAlertCard(a)).filter(Boolean);
  const watchlists: WatchlistCardRef[] = overview?.watchlists ?? [];

  return (
    <aside className="bt-pane" aria-label="Alerts">
      <div className="bt-bar">
        <div className="bt-title">
          <b>Alerts</b>
          <span className="dim"> · run in the AnalystBro desktop app · made by the agent</span>
        </div>
        <button className="icon-btn" onClick={onClose} aria-label="Close alerts">
          <CloseIcon />
        </button>
      </div>
      <div className="bt-tabs" role="tablist">
        {(["alerts", "watchlists"] as const).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? "on" : ""}
            onClick={() => { setTab(t); setOpen(null); }}>
            {t === "alerts" ? `Alerts${overview ? ` (${alerts.length})` : ""}` : `Watchlists${overview ? ` (${watchlists.length})` : ""}`}
          </button>
        ))}
      </div>
      <div className="bt-body">
        {err && <div className="bt-error">{err}</div>}
        {!overview && !err && <div className="dim">Loading…</div>}
        {overview && open?.kind === "alert" && (
          <AlertDetail alertId={open.id} onBack={() => setOpen(null)} onChanged={load} />
        )}
        {overview && open?.kind === "watchlist" && (
          <WatchlistDetail watchlistId={open.id} onBack={() => setOpen(null)}
            onOpenAlert={(id) => { setTab("alerts"); setOpen({ kind: "alert", id }); }} />
        )}
        {overview && !open && tab === "alerts" && (
          <div className="al-list">
            {alerts.length === 0 && (
              <p className="dim">No alerts yet. Ask the agent in chat, e.g. "alert me when any bank in my watchlist closes above its 200-day average".</p>
            )}
            {alerts.map((a) => (
              <AlertCard key={a.alertId} card={a} onOpen={(id) => setOpen({ kind: "alert", id })} />
            ))}
          </div>
        )}
        {overview && !open && tab === "watchlists" && (
          <div className="al-list">
            {watchlists.length === 0 && <p className="dim">No watchlists yet. Ask the agent in chat to make one.</p>}
            {watchlists.map((w) => (
              <WatchlistCard key={w.watchlistId} card={w} onOpen={(id) => setOpen({ kind: "watchlist", id })} />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}

function AlertDetail({ alertId, onBack, onChanged }: { alertId: string; onBack: () => void; onChanged: () => void }) {
  const client = useDataClient();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => client.alert(alertId).then(setD).catch((e) => setErr(String(e)));
  useEffect(() => {
    setD(null);
    load();
    return client.onAlertsUpdate(load);
  }, [alertId]);
  const card = d?.card ? readAlertCard(d.card) : undefined;
  const act = (action: "pause" | "resume") => {
    setBusy(true);
    client.setAlert(alertId, action).then(() => { load(); onChanged(); }).catch((e) => setErr(String(e))).finally(() => setBusy(false));
  };
  if (err) return <div className="bt-error">{err}</div>;
  if (!d || !card) return <div className="dim">Loading…</div>;
  const rule = d.rule ?? {};
  return (
    <div className="al-detail">
      <button className="al-back" onClick={onBack}>← All alerts</button>
      <h3>{card.name}</h3>
      <div className="al-meta">
        <span className={`bt-card-kind alert ${card.route}`}>{routeLabel(card.route)}</span>
        <span>{card.schedule}</span>
        <span>{card.status}</span>
        <span className="dim">to {channels(card)}</span>
      </div>
      <div className="al-section">
        <span className="dim">{rule.kind === "prompt" ? "Prompt" : "Rule"}</span>
        {rule.kind === "prompt" && <p className="al-prompt">{rule.text}</p>}
        {rule.kind === "code" && <pre className="al-code">{rule.text}</pre>}
        {rule.kind === "strategy" && <p>Saved strategy {rule.strategyId} (v{rule.version}): fires when it orders on the newest bar.</p>}
        {rule.kind === "builtin" && <p>{builtinText(rule.category, rule.criteria)}</p>}
      </div>
      <div className="al-section">
        <span className="dim">Covers {d.covers.length} symbol{d.covers.length === 1 ? "" : "s"}</span>
        <p className="al-symbols">{d.covers.join(", ") || "—"}</p>
      </div>
      {client.capabilities.controlAlerts && (
        <div className="pa-actions">
          {card.status === "active" ? (
            <button disabled={busy} onClick={() => act("pause")}>Pause</button>
          ) : (
            <button disabled={busy} onClick={() => act("resume")}>Resume</button>
          )}
          <span className="dim">To change the rule or delete it, ask the agent in chat.</span>
        </div>
      )}
      {client.recipients && <Recipients alertId={alertId} emailOn={card.email} />}
      <div className="bt-tablewrap">
        <table className="bt-table">
          <caption className="dim" style={{ textAlign: "left" }}>Recent runs</caption>
          <thead><tr><th>When</th><th>Result</th><th>Message</th></tr></thead>
          <tbody>
            {d.runs.length === 0 && <tr><td colSpan={3} className="dim">Not run yet.</td></tr>}
            {d.runs.map((r: any, i: number) => (
              <tr key={i}>
                <td>{ago(r.ts)}</td>
                <td>{r.status === "failed" ? "failed" : r.status === "skipped" ? "skipped" : r.fired ? "fired" : "nothing"}</td>
                <td className="al-msg">{r.result?.body ?? r.result?.error ?? r.result?.reason ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  pending: "awaiting confirmation", confirmed: "confirmed", unsubscribed: "unsubscribed", bounced: "bounced",
};

/** Extra email recipients (Stock-Screener doc 288): each address confirms once by email before
 * it gets anything, and can unsubscribe. The only editable list in the pane. */
function Recipients({ alertId, emailOn }: { alertId: string; emailOn: boolean }) {
  const api = useDataClient().recipients!;
  const [d, setD] = useState<{ synced: boolean; unavailable?: boolean; recipients: Recipient[]; count?: number; cap?: number } | null>(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const load = () => api.list(alertId).then((r) => { setD(r); setErr(""); }).catch((e) => setErr(msg(e)));
  useEffect(() => {
    setD(null);
    setNote("");
    load();
  }, [alertId]);
  const run = (p: Promise<void>, done?: string) => {
    setBusy(true);
    setErr("");
    p.then(() => { if (done) setNote(done); return load(); }).catch((e) => setErr(msg(e))).finally(() => setBusy(false));
  };
  const add = () => {
    const e = email.trim();
    if (!e) return;
    run(api.add(alertId, e).then(() => setEmail("")), `Confirmation email sent to ${e}. They get this alert only after confirming.`);
  };
  const cap = d?.cap ?? 10;
  const count = d?.count ?? d?.recipients.filter((r) => r.status !== "unsubscribed" && r.status !== "bounced").length ?? 0;
  return (
    <div className="al-section al-recipients">
      <span className="dim">
        Also email{d?.synced ? ` · ${count}/${cap}` : ""}
      </span>
      {!d && !err && <p className="dim">Loading…</p>}
      {d && !d.synced && d.unavailable && <p className="dim">Email recipients aren't available yet.</p>}
      {d && !d.synced && !d.unavailable && <p className="dim">Syncing… recipients can be added once this alert reaches AnalystBro.</p>}
      {d?.synced && (
        <>
          {!emailOn && <p className="dim">Email is off for this alert: nobody gets email until it's turned on.</p>}
          <div className="al-chips">
            {d.recipients.length === 0 && <span className="dim">Only you.</span>}
            {d.recipients.map((r) => (
              <span key={r.id} className={`al-chip ${r.status}`} title={STATUS_LABEL[r.status] ?? r.status}>
                <span className="al-chip-email">{r.email}</span>
                <span className="al-chip-status">{STATUS_LABEL[r.status] ?? r.status}</span>
                {r.status === "pending" && r.can_resend && (
                  <button className="al-chip-act" disabled={busy} onClick={() => run(api.resend(alertId, r.id), `Confirmation email sent again to ${r.email}.`)}>
                    Resend
                  </button>
                )}
                {r.status !== "unsubscribed" && r.status !== "bounced" && (
                  <button className="al-chip-x" disabled={busy} aria-label={`Remove ${r.email}`} onClick={() => run(api.remove(alertId, r.id))}>
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
          {count < cap && (
            <form className="al-chip-add" onSubmit={(e) => { e.preventDefault(); add(); }}>
              <input type="email" placeholder="name@example.com" value={email} disabled={busy}
                onChange={(e) => setEmail(e.target.value)} aria-label="Email address to add" />
              <button type="submit" disabled={busy || !email.trim()}>Add</button>
            </form>
          )}
          <p className="dim al-chip-help">Each address gets one confirmation email and receives alerts only after confirming. They can unsubscribe from any email.</p>
        </>
      )}
      {note && <p className="al-chip-note">{note}</p>}
      {err && <div className="bt-error">{err}</div>}
    </div>
  );
}

function msg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function builtinText(category: string, c: any): string {
  if (category === "analyst_follow") return "Fires when the analyst you follow makes a new prediction.";
  if (category === "screener") return "Fires when a stock joins or leaves the watchlist.";
  if (category === "quantitative" && c?.alertType === "price_deviation")
    return `Fires when a symbol's daily change is ${c.operator === "<" || c.operator === "less_than" ? "below" : "above"} ${c.value}%.`;
  return "A built-in rule.";
}

function WatchlistDetail({ watchlistId, onBack, onOpenAlert }: { watchlistId: string; onBack: () => void; onOpenAlert: (id: string) => void }) {
  const client = useDataClient();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null);
    client.watchlist(watchlistId).then(setD).catch((e) => setErr(String(e)));
  }, [watchlistId]);
  if (err) return <div className="bt-error">{err}</div>;
  if (!d) return <div className="dim">Loading…</div>;
  return (
    <div className="al-detail">
      <button className="al-back" onClick={onBack}>← All watchlists</button>
      <h3>{d.card?.name}</h3>
      <p className="al-symbols">{(d.symbols ?? []).join(", ") || "No symbols"}</p>
      <div className="al-section">
        <span className="dim">Alerts on this watchlist</span>
        {(d.usedBy ?? []).length === 0 && <p className="dim">None.</p>}
        {(d.usedBy ?? []).map((a: any) => (
          <button key={a.alertId} className="al-link" onClick={() => onOpenAlert(a.alertId)}>
            {a.name} <span className="dim">· {a.status}</span>
          </button>
        ))}
      </div>
      <p className="dim">To add or remove symbols, ask the agent in chat.</p>
    </div>
  );
}
